import React from 'react';
import { Car, Plane, UserCog, Camera, Building2, Handshake } from 'lucide-react';
import IntakeForm from './IntakeForm';

const COMMON_CONTACT = [
  { name: 'client_name', label: 'Full name', required: true, placeholder: 'Jane Smith' },
  { name: 'client_email', label: 'Email', required: true, type: 'email', placeholder: 'you@email.com' },
  { name: 'client_phone', label: 'Mobile phone', required: true, placeholder: '(404) 555-0100' },
];

// ─── 1. Self-Drive Booking Request ─────────────────────────────────────────
export const BookingRequestPage = () => (
  <IntakeForm
    intakeType="booking-request"
    icon={Car}
    title="Self-Drive Rental Request"
    subtitle="Premium and exotic vehicles. All rentals are request-based and require ID + insurance verification."
    testid="page-booking-request"
    fields={[
      ...COMMON_CONTACT,
      { name: 'event_date', label: 'Pickup date', required: true, type: 'date' },
      { name: 'event_end_date', label: 'Return date', type: 'date' },
      { name: 'pickup_time', label: 'Pickup time', type: 'time' },
      { name: 'pickup_address', label: 'Delivery address (optional)', placeholder: 'Atlanta, GA — leave blank for our location' },
      { name: 'vehicle_preference', label: 'Vehicle you want', required: true, placeholder: 'e.g., Lamborghini Urus, Rolls Wraith, Porsche 911 Turbo S' },
      { name: 'driver_age', label: 'Driver age', type: 'number', placeholder: '25+' },
      { name: 'broker_code', label: 'Broker / referral code', placeholder: 'Optional' },
      { name: 'notes', label: 'Anything else we should know?', type: 'textarea', placeholder: 'Special occasion, additional drivers, delivery needs…', span: 'full' },
    ]}
    sideNote="Minimum age 25, valid driver's license, current full-coverage personal insurance, and a refundable security deposit are required for all self-drive rentals."
  />
);

// ─── 2. Chauffeur Service ──────────────────────────────────────────────────
export const ChauffeurRequestPage = () => (
  <IntakeForm
    intakeType="chauffeur-request"
    icon={UserCog}
    title="Chauffeur Service Request"
    subtitle="Hourly, point-to-point, or full-day VIP transport with a vetted professional driver."
    testid="page-chauffeur-request"
    fields={[
      ...COMMON_CONTACT,
      { name: 'event_date', label: 'Service date', required: true, type: 'date' },
      { name: 'pickup_time', label: 'Pickup time', required: true, type: 'time' },
      { name: 'hours', label: 'Estimated hours', required: true, type: 'number', placeholder: '4' },
      { name: 'passengers', label: 'Passengers', type: 'number', placeholder: '2' },
      { name: 'pickup_address', label: 'Pickup address', required: true, placeholder: 'Starting point' },
      { name: 'dropoff_address', label: 'Drop-off (or "Multiple stops")', placeholder: 'Final destination or "Hourly"' },
      { name: 'vehicle_preference', label: 'Vehicle preference', type: 'select', options: [
        'No preference', 'Executive Sedan', 'Luxury SUV', 'Sprinter Van', 'Stretch Limo', 'Exotic',
      ] },
      { name: 'occasion', label: 'Occasion', type: 'select', options: [
        'Business', 'Date Night', 'Wedding', 'Concert / Game', 'Bachelor / Bachelorette', 'Other',
      ] },
      { name: 'notes', label: 'Itinerary notes', type: 'textarea', placeholder: 'Stops, special requests, dress code…', span: 'full' },
    ]}
  />
);

// ─── 3. Airport Transfer ───────────────────────────────────────────────────
export const AirportTransferPage = () => (
  <IntakeForm
    intakeType="airport-transfer"
    icon={Plane}
    title="Airport Transfer"
    subtitle="ATL Hartsfield-Jackson, PDK, FTY and surrounding executive airports. Curbside or FBO meet-and-greet."
    testid="page-airport-transfer"
    fields={[
      ...COMMON_CONTACT,
      { name: 'transfer_direction', label: 'Direction', required: true, type: 'select', options: [
        { value: 'arrival', label: 'Arrival — picking up at airport' },
        { value: 'departure', label: 'Departure — drop off at airport' },
        { value: 'roundtrip', label: 'Round trip' },
      ] },
      { name: 'event_date', label: 'Flight date', required: true, type: 'date' },
      { name: 'flight_time', label: 'Flight time', required: true, type: 'time' },
      { name: 'flight_number', label: 'Flight number', placeholder: 'DL1234' },
      { name: 'airport', label: 'Airport', type: 'select', options: [
        { value: 'ATL', label: 'ATL · Hartsfield-Jackson' },
        { value: 'PDK', label: 'PDK · Peachtree-DeKalb' },
        { value: 'FTY', label: 'FTY · Fulton County' },
        { value: 'OTHER', label: 'Other' },
      ] },
      { name: 'passengers', label: 'Passengers', type: 'number', placeholder: '2' },
      { name: 'pickup_address', label: 'Other end (home / hotel)', required: true, placeholder: 'Home address or hotel name' },
      { name: 'vehicle_preference', label: 'Vehicle', type: 'select', options: ['Executive Sedan', 'Luxury SUV', 'Sprinter Van', 'Exotic'] },
      { name: 'meet_and_greet', label: 'Inside meet-and-greet?', type: 'select', options: [
        { value: 'curbside', label: 'Curbside is fine' },
        { value: 'meet_inside', label: 'Yes, meet me at baggage claim' },
        { value: 'fbo', label: 'FBO / Private terminal' },
      ] },
      { name: 'notes', label: 'Special requests', type: 'textarea', placeholder: 'Car seats, oversized luggage, additional stops…', span: 'full' },
    ]}
  />
);

// ─── 4. Event / Photoshoot ─────────────────────────────────────────────────
export const EventBookingPage = () => (
  <IntakeForm
    intakeType="event-booking"
    icon={Camera}
    title="Event, Photoshoot & Production"
    subtitle="Music videos, brand campaigns, weddings, proposals, content shoots. Vehicles on-set with professional handling."
    testid="page-event-booking"
    fields={[
      ...COMMON_CONTACT,
      { name: 'company', label: 'Company / brand', placeholder: 'Production company or brand' },
      { name: 'event_type', label: 'Event type', required: true, type: 'select', options: [
        'Music Video', 'Commercial / Brand Shoot', 'Wedding', 'Proposal', 'Photoshoot', 'Editorial', 'Corporate Event', 'Other',
      ] },
      { name: 'event_date', label: 'Shoot / event date', required: true, type: 'date' },
      { name: 'hours', label: 'Estimated hours on set', type: 'number', placeholder: '6' },
      { name: 'pickup_address', label: 'Location / set address', placeholder: 'Studio or location' },
      { name: 'vehicle_preference', label: 'Vehicle(s) needed', required: true, placeholder: 'e.g., 1 Lambo Urus + 1 Rolls Wraith', span: 'full' },
      { name: 'usage_rights', label: 'Usage rights needed?', type: 'select', options: [
        { value: 'none', label: 'No commercial use' },
        { value: 'social', label: 'Social media only' },
        { value: 'commercial', label: 'Full commercial / paid media' },
      ] },
      { name: 'budget', label: 'Budget range', type: 'select', options: ['Under $2k', '$2k–$5k', '$5k–$10k', '$10k+', 'Open'] },
      { name: 'notes', label: 'Creative direction / details', type: 'textarea', placeholder: 'Treatment, mood, special requirements, COI insured set, etc.', span: 'full' },
    ]}
    sideNote="A Certificate of Insurance (COI) is required for all on-set shoots. We can provide a sample COI form on request."
  />
);

// ─── 5. Owner Onboarding ───────────────────────────────────────────────────
export const OwnerOnboardingPage = () => (
  <IntakeForm
    intakeType="owner-onboarding"
    icon={Building2}
    title="List Your Vehicle With ACE"
    subtitle="Turn your idle luxury or exotic vehicle into monthly cash flow. We handle bookings, vetting, insurance compliance, maintenance tracking, and payouts."
    testid="page-owner-onboarding"
    fields={[
      ...COMMON_CONTACT,
      { name: 'company', label: 'LLC / business name', placeholder: 'If owned by an entity' },
      { name: 'vehicle_year', label: 'Year', required: true, type: 'number', placeholder: '2023' },
      { name: 'vehicle_make', label: 'Make', required: true, placeholder: 'Lamborghini' },
      { name: 'vehicle_model', label: 'Model', required: true, placeholder: 'Urus' },
      { name: 'mileage', label: 'Current mileage', type: 'number', placeholder: '12,000' },
      { name: 'estimated_value', label: 'Estimated value ($)', type: 'number', placeholder: '280000' },
      { name: 'condition', label: 'Condition', type: 'select', options: ['Showroom', 'Excellent', 'Very Good', 'Good', 'Project'] },
      { name: 'availability_days', label: 'Days/month available', type: 'number', placeholder: '25' },
      { name: 'revenue_share_percent', label: 'Owner revenue share %', type: 'number', placeholder: '70', hint: 'Default 70/30 owner/ACE — negotiable based on vehicle class.' },
      { name: 'has_loan', label: 'Lien / loan on vehicle?', type: 'select', options: [
        { value: 'no', label: 'No — owned outright' },
        { value: 'yes', label: 'Yes — financed' },
      ] },
      { name: 'insurance', label: 'Current insurance', type: 'select', options: [
        { value: 'personal', label: 'Personal only' },
        { value: 'commercial', label: 'Commercial / rental-rated' },
        { value: 'none', label: 'Need recommendation' },
      ] },
      { name: 'notes', label: 'Tell us about the car & your goals', type: 'textarea', placeholder: 'Mods, history, target income, etc.', span: 'full' },
    ]}
    sideNote="ACE only lists vehicles that pass our 50-point inspection. Approved owners receive monthly revenue statements + 1099-NEC for tax filing."
  />
);

// ─── 6. Broker / Affiliate Submission ──────────────────────────────────────
export const BrokerSubmissionPage = () => (
  <IntakeForm
    intakeType="broker-submission"
    icon={Handshake}
    title="Broker · Submit a Client"
    subtitle="Refer luxury rental or chauffeur clients to ACE and earn a commission on every completed booking. Simple, transparent, paid net 7."
    testid="page-broker-submission"
    fields={[
      { name: 'client_name', label: 'Your name (broker)', required: true, placeholder: 'Your full name' },
      { name: 'broker_name', label: 'Business / brokerage name', placeholder: 'e.g., Red Key Club ATL' },
      { name: 'client_email', label: 'Your email', required: true, type: 'email' },
      { name: 'client_phone', label: 'Your phone', required: true },
      { name: 'broker_code', label: 'Existing broker code (if any)', placeholder: 'Leave blank for new accounts' },
      { name: 'commission_rate', label: 'Requested commission %', type: 'number', placeholder: '10', hint: 'Standard 10%. Negotiable for repeat brokers.' },
      // Referred client
      { name: 'submitted_client_name', label: 'Client full name', required: true, placeholder: 'Person you are referring', span: 'full' },
      { name: 'submitted_client_email', label: 'Client email', type: 'email' },
      { name: 'submitted_client_phone', label: 'Client phone' },
      { name: 'submitted_service_type', label: 'Service type', required: true, type: 'select', options: [
        { value: 'rental', label: 'Self-drive Rental' },
        { value: 'chauffeur', label: 'Chauffeur' },
        { value: 'transfer', label: 'Airport Transfer' },
        { value: 'event', label: 'Event / Photoshoot' },
      ] },
      { name: 'event_date', label: 'Service date', type: 'date' },
      { name: 'vehicle_preference', label: 'Vehicle / package', placeholder: 'e.g., Rolls Wraith — full weekend' },
      { name: 'notes', label: 'Anything else?', type: 'textarea', placeholder: 'Budget, occasion, urgency…', span: 'full' },
    ]}
    sideNote="By submitting, you confirm you have the right to refer this client. Commissions paid net 7 days after the booking is completed and fully paid."
  />
);
